import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import framePortraitSvg from 'assets/icons/frame-portrait.svg';
import { removeColumnsFields } from 'utils/removeColumnsFields';
import Modal from 'lib/Modal';
import Table from 'lib/Table/Table';
import { TableHeight } from 'lib/Table/Table.types';
import { Store } from 'components/common/types/Store.types';
import { Asset } from 'components/common/types/Asset.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { getIsReseller } from 'store/publisher/selectors';
import { DealType } from 'components/common/types/Deal.types';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getDischargedFrameIdsSet } from 'components/pages/DealWithLines/common/utils';
import { FrameListExportButton } from './FrameListExportButton';
import { getColumnDefs } from './frameDetailsColumns';
import { useLazyFrameListDatasource } from './useLazyFrameListDatasource';

type Props = {
  assets: Asset[];
  isOpen: boolean;
  onClose: VoidFunction;
  footer?: JSX.Element | null;
  hasExportButton?: boolean;
};

export const FrameDetailsModal = ({
  assets,
  isOpen = false,
  onClose,
  footer: Footer = null,
  hasExportButton = false,
}: Props): React.ReactElement => {
  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);
  const dischargedFrameIds = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.dischargedFrameIds,
  );
  const isReseller = useSelector(getIsReseller);
  const isDealTypeGuaranteed = useSelector(getDealType) === DealType.GUARANTEED;
  const hasVisualUnitsEnabled = useHasFeatureAccess(FeatureFlags.VISUAL_UNITS);

  const assetsWithOOC = useMemo(() => {
    if (!isOpen) return [];

    const dischargedIds = getDischargedFrameIdsSet(dischargedFrameIds);

    return assets
      .map((asset) => ({
        ...asset,
        isDischarged: dischargedIds.has(asset.frameId),
      }))
      .sort((a, b) => {
        if (a.availability === 'Available' && b.availability !== 'Available') return -1;
        if (a.availability !== 'Available' && b.availability === 'Available') return 1;

        return b.impressions - a.impressions;
      });
  }, [assets, dischargedFrameIds, isOpen]);

  const { extraGridOptions: lazyGridOptions, getTableApi } = useLazyFrameListDatasource({
    assets: assetsWithOOC,
    isOpen,
  });

  const fieldsToRemove = useMemo(
    () =>
      assetsWithOOC.reduce(
        (acc, { availability, impressions, sot }) => ({
          ...acc,
          availability: acc.availability && !availability,
          impressions: acc.impressions && !impressions,
          sot: acc.sot && !sot,
        }),
        {
          mediaOwner: !isReseller,
          visualUnitCode: !hasVisualUnitsEnabled,
          availability: !isDealTypeGuaranteed,
          impressions: !isDealTypeGuaranteed,
          sot: !isDealTypeGuaranteed,
          vioohId: !assetsWithOOC.some((asset) => asset.vioohId),
        },
      ),
    [assetsWithOOC, isReseller, hasVisualUnitsEnabled, isDealTypeGuaranteed],
  );

  return (
    <Modal
      customHeaderComponent={
        <div className="flex items-center w-11/12 xl:w-300">
          <SVG src={framePortraitSvg} className="text-primary" />
          <div className="ml-3 sub-header-lg text-neutral-900">Frame details</div>
          {hasExportButton && <FrameListExportButton assets={assets} />}
        </div>
      }
      customChildrenClasses="!overflow-hidden"
      dataTestId="frame-details-modal"
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex h-popupContent flex-col">
        <div className="min-h-0 flex-1">
          <Table
            extraGridOptions={{
              enableCellTextSelection: true,
              ...lazyGridOptions,
            }}
            columnDefs={removeColumnsFields({
              columns: getColumnDefs(currencyCode, localeCode),
              fields: fieldsToRemove,
            })}
            getTableApi={getTableApi}
            tableHeight={TableHeight.FILL_PARENT}
          />
        </div>
        {Footer}
      </div>
    </Modal>
  );
};
